import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';

import { aiReferral } from './ai-referral';

// Describes an outbound affiliate click, passed by the "Book" / "Get tickets"
// CTAs so Umami can report which listings drive click-throughs — the
// revenue-relevant signal a pageview-only tool can't give us. The listing id
// and locale are also in the page URL Umami records automatically, but sending
// them (plus `source`, which the URL doesn't carry) as event properties makes
// them filterable/groupable in the dashboard without URL parsing.
export type OutboundMeta = {
  // Which catalogue the listing is from — 'hotel' is a "where to stay" search
  // (the app's src/lib/hotels.ts) from an event, article or city page.
  kind: 'tour' | 'event' | 'boat' | 'hotel';
  // Affiliate program the click monetizes through — `tours.source` /
  // `events.source` / `boats.source` (e.g. 'viator', 'awin_fnac', 'click_and_boat'),
  // or 'stay22' for hotels.
  source: string;
  // Stable per-listing id (tour.id / event.id): the join key back to the row.
  // For a hotel search, the page it came from (event.id, article._id, city id).
  id: string;
  // Localized listing title, for human-readable dashboard rows (truncated).
  title?: string | null;
  // city_id and UI locale, so clicks can be sliced by place and language.
  city?: string | null;
  locale?: string;
};

declare global {
  interface Window {
    // Umami's tracker global (injected in the app's src/app/+html.tsx). Absent until the
    // deferred script loads, and never present on native — always guard it.
    umami?: { track: (event: string, data?: Record<string, unknown>) => void };
  }
}

// Record a single Umami "affiliate-click" event. No-op whenever the tracker
// isn't there — native, dev/local builds (no website id), or a blocked script —
// so callers never have to guard.
function trackOutbound(meta: OutboundMeta) {
  if (typeof window === 'undefined' || !window.umami?.track) return;
  // The AI assistant the visit came from (chatgpt, perplexity, …) — see
  // ai-referral.ts.
  const via = aiReferral();
  window.umami.track('affiliate-click', {
    kind: meta.kind,
    source: meta.source,
    id: meta.id,
    ...(meta.title ? { title: meta.title.slice(0, 100) } : {}),
    ...(meta.city ? { city: meta.city } : {}),
    ...(meta.locale ? { locale: meta.locale } : {}),
    ...(via ? { via } : {}),
  });
}

// Opens an external URL.
//
// - Web: a new browser TAB (not a popup window), with the opener severed and
//   rel="sponsored nofollow" so affiliate/paid links follow Google's link
//   guidance. Your app stays open in the original tab.
// - Native (iOS/Android): the in-app browser (SFSafariViewController /
//   Chrome Custom Tabs) via expo-web-browser.
//
// Pass `meta` for affiliate CTAs and the click is recorded in Umami (web only;
// silently skipped without `meta` or on native).
export function openExternal(url: string, meta?: OutboundMeta) {
  if (meta) trackOutbound(meta);
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer sponsored nofollow';
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  }
  WebBrowser.openBrowserAsync(url);
}
